import { useProject } from "../context/ProjectContext";
import { useUserSettings } from "../context/UserSettingsContext";
import {
  getEstimateReportData,
  getGCGRReportData,
  getContractingReportData,
  getCostModelReportData,
} from "../../excel/workbook";
import { PROJECT_REPORT_POPULATE } from "../../excel/datasources/reportTables";
import {
  readCardSummary,
  saveContractingCardSummary,
  saveCostModelCardSummary,
  saveEstimateCardSummary,
  saveGcgrCardSummary,
} from "../../excel/datasources/cardSummaries";
import Logger from "../../clients/sentry";
import { serializeErrorForWarning } from "../../errors";
import { retireLegacyCacheKeys } from "../../excel/lifecycle";
import { getLoadedProjectId } from "../../excel/workbook/state";
import { refreshEstimateDatasource } from "../../excel/datasources/estimate";
import { refreshContractingDatasource } from "../../excel/datasources/bidTabs";
import { fetchProjectById, refreshGCGRCaches } from "../../api";
import { refreshCostModelCaches } from "../../api/costModels";
import { COST_MODEL_ENABLED } from "../../config/features";
import { LoadedDatasourceKey, browserStore, loadedDatasourceStore } from "../../utils/storageKeys";

export function useWorkbookRestore() {
  const {
    selectedProject,
    setSelectedProject,
    selectedEstimate,
    setSelectedEstimate,
    setSelectedGcgr,
    setIsContractingLoaded,
    setSelectedCostModel,
  } = useProject();
  const { settingsLoaded } = useUserSettings();

  const restoreFromWorkbook = async (): Promise<void> => {
    // The settings read the workbook on mount, and Excel work mustn't overlap, so this waits.
    await settingsLoaded;

    await retireLegacyCacheKeys();

    if (selectedProject) return;
    const projectId = await getLoadedProjectId();
    if (!projectId) return;

    // Each datasource restores from its own .metadata row: the row is what the workbook actually
    // holds data for. Excel reads, so one after another.
    const estimateRow = selectedEstimate ? undefined : (await getEstimateReportData())[0];
    const gcgrRow = (await getGCGRReportData())[0];
    const contractingRow = (await getContractingReportData())[0];
    const costModelRow = COST_MODEL_ENABLED ? (await getCostModelReportData())[0] : undefined;

    const estimateId = estimateRow?.project_id === projectId ? estimateRow.estimate_id : undefined;
    const gcgrId = gcgrRow?.project_id === projectId ? gcgrRow.gcgr_id : undefined;
    const hasContracting = contractingRow?.project_id === projectId;
    const costModelId = costModelRow?.project_id === projectId ? costModelRow.cost_model_id : undefined;

    // Set from the rows before anything is fetched, so logs during the reload, including a failed
    // fetch, say what the workbook has loaded.
    const syncKey = (key: LoadedDatasourceKey, value: unknown) =>
      value ? loadedDatasourceStore.set(key, String(value)) : loadedDatasourceStore.remove(key);
    syncKey("selectedProjectId", projectId);
    if (!selectedEstimate) syncKey("selectedEstimateId", estimateId);
    syncKey("selectedGcgrId", gcgrId);
    syncKey("contractingLoaded", hasContracting);
    syncKey("selectedCostModelId", costModelId);

    // Each loaded datasource's payload is fetched exactly as selecting it does, and the loading
    // screen waits for it; its lookups finish in the background, since no card or report row needs
    // them. API requests only, so they run together. A failed fetch restores nothing and clears
    // nothing — a transient API failure must not discard a valid selection.
    const [project, estimate, gcgr, costModel, contracting] = await Promise.all([
      fetchProjectById(projectId, { populate: PROJECT_REPORT_POPULATE }),
      estimateId ? refreshEstimateDatasource(projectId, estimateId, { awaitLookups: false }) : undefined,
      gcgrId ? refreshGCGRCaches(projectId, gcgrId, { awaitLookups: false }) : undefined,
      costModelId ? refreshCostModelCaches(projectId, costModelId) : undefined,
      hasContracting ? refreshContractingDatasource(projectId) : undefined,
    ]);
    // A 401 in any of them signed the user out; selecting nothing lets the next login restore.
    if (!browserStore.get("xAuthToken")) return;
    // Thrown, so the caller says the project couldn't load rather than silently showing the picker.
    if (!project.success) throw project.error;
    if (!project.data) throw new Error("The workbook's project could not be loaded");
    setSelectedProject(project.data);

    const estimateRecord = estimate?.success ? estimate.data?.estimate : undefined;
    if (estimateRecord) setSelectedEstimate(estimateRecord);
    if (gcgr?.success && gcgr.data) setSelectedGcgr(gcgr.data);
    const bidGroups = contracting?.success ? (contracting.data?.bidGroups ?? []) : [];
    if (bidGroups.length > 0) setIsContractingLoaded(true);
    // The record alone stages nothing: refreshCostModelCaches saves it only alongside its totals.
    const costModelTotals = costModel?.totals?.success ? costModel.totals.data : undefined;
    const costModelRecord = costModel?.costModel.success && costModelTotals ? costModel.costModel.data : undefined;
    if (costModelRecord) setSelectedCostModel(costModelRecord);

    try {
      if (estimateRecord && (await readCardSummary("estimate"))?.id !== estimateRecord.id) {
        await saveEstimateCardSummary(estimateRecord, String(estimateRow?.milestone ?? ""), estimateRow?.last_updated);
      }
      if (gcgr?.success && gcgr.data && (await readCardSummary("gcgr"))?.id !== gcgr.data.id) {
        await saveGcgrCardSummary(gcgr.data, gcgrRow?.last_updated);
      }
      if (bidGroups.length > 0 && !(await readCardSummary("contracting"))) {
        await saveContractingCardSummary(bidGroups, contractingRow?.last_updated);
      }
      if (costModelRecord && costModelTotals && (await readCardSummary("cost-model"))?.id !== costModelRecord.id) {
        await saveCostModelCardSummary(costModelRecord, costModelTotals, costModelRow?.last_updated);
      }
    } catch (error) {
      Logger.warn("Could not write a missing card summary on restore", serializeErrorForWarning(error));
    }
  };

  return { restoreFromWorkbook };
}
